import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { AddedToCartModal } from '@/components/cart/added-to-cart-modal';
import { RelatedProducts } from '@/components/product/related-products';
import { 
  Heart, 
  ChevronLeft, 
  ChevronRight,
  Plus, 
  Minus, 
  ShoppingCart,
  Package,
  Truck,
  Shield,
  AlertCircle,
  Share2,
  X,
  Check,
  Zap
} from 'lucide-react';
import { Product, SelectedVariation } from '@/types';
import { useStoreInfo } from '@/lib/store';
import { useStore } from '@/store';
import { formatPrice, getDomain } from '@/lib/utils';
import { calculateProductDiscount, getEffectiveVariationPrice } from '@/lib/discount';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Thumbs, FreeMode } from 'swiper/modules';
import { ProductDetailSkeleton } from '@/components/ui/skeleton';
import { NoImagePlaceholder } from '@/components/ui/no-image-placeholder';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';

export function ModernProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariations, setSelectedVariations] = useState<SelectedVariation[]>([]);
  const [showAddedToCart, setShowAddedToCart] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [zoomImage, setZoomImage] = useState<string>('');
  const [zoomImageIndex, setZoomImageIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState<Set<number>>(new Set());
  const swiperRef = useRef<any>(null);
  
  const addToCart = useStore((state) => state.addToCart);
  const wishlist = useStore((state) => state.wishlist);
  const toggleWishlist = useStore((state) => state.toggleWishlist);
  const { storeInfo, openLockNotice } = useStoreInfo();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const domain = getDomain();
        const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/products?domain=${domain}`);
        const data = await response.json();
        
        if (data.status) {
          // Store all products for related products component
          setAllProducts(data.products || []);
          
          const foundProduct = data.products.find((p: Product) => p.id === parseInt(id!));
          if (foundProduct) {
            setProduct(foundProduct);
            
            if (foundProduct.variations && foundProduct.variations.length > 0) {
              const variationImages = foundProduct.variations.map((v: any) => v.variation_img);
              const variationNames = foundProduct.variations.map((v: any) => v.variation);
              const uniqueNames = new Set(variationNames);

              const normalizeVariation = (v: any) => ({
                v_id: v.v_id,
                variation: v.variation,
                quantity: 0,
                variation_amount: v.variation_amount,
                variation_img: v.variation_img,
                hasQuantityPricing: !!v.hasQuantityPricing,
                quantityPricing: Array.isArray(v.quantityPricing) ? v.quantityPricing : []
              });

              if (variationNames.length !== uniqueNames.size) {
                const seenNames = new Set<string>();
                const uniqueVariations = foundProduct.variations.filter((v: any) => {
                  if (seenNames.has(v.variation)) {
                    return false;
                  }
                  seenNames.add(v.variation);
                  return true;
                });

                setSelectedVariations(uniqueVariations.map(normalizeVariation));
              } else {
                setSelectedVariations(foundProduct.variations.map(normalizeVariation));
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleAddToCart = () => {
    if (storeInfo?.site_lock === 1) {
      openLockNotice();
      return;
    }
    if (isAdding) return; // prevent multiple rapid clicks
    setIsAdding(true);
    if (!product) return;

    const hasVariations = product.variations && product.variations.length > 0;
    const hasRealVariations = hasVariations && product.variations!.some(v => v.variation.toLowerCase() !== 'nil');
    
    if (hasRealVariations) {
      const activeVariations = selectedVariations.filter(v => v.quantity > 0);
      if (activeVariations.length === 0) {
        toast.error('Please select at least one variation', {
          icon: <AlertCircle className="h-5 w-5" />,
          duration: 3000,
        });
        return;
      }
      
      addToCart({
        ...product,
        quantity: 0,
        selectedVariations: activeVariations
      });
    } else {
      addToCart({
        ...product,
        quantity,
        selectedVariations: []
      });
    }
    // Show a prominent bottom toast so user will take note
    toast.success('Added to cart', {
      duration: 3200,
      position: 'bottom-center',
      style: {
        minWidth: '320px',
        maxWidth: '520px',
        borderRadius: '14px',
        padding: '18px 20px',
        fontSize: '1rem',
        background: 'linear-gradient(90deg, #C41E2E, #EA5456)',
        color: '#fff',
        boxShadow: '0 12px 28px rgba(0,0,0,0.25)'
      },
      icon: '🛒'
    });

    // Keep mobile embedded add confirmation UI for immediate visual feedback
    setShowAddedToCart(true);
    setTimeout(() => setShowAddedToCart(false), 3000);

    // Re-enable button after short delay
    setTimeout(() => setIsAdding(false), 1500);
  };

  const updateVariationQuantity = (variationName: string, change: number) => {
    setSelectedVariations(prev =>
      prev.map(v => {
        if (v.variation === variationName) {
          const newQuantity = Math.max(0, v.quantity + change);
          return { ...v, quantity: newQuantity };
        }
        return v;
      })
    );
  };

  const setVariationQuantity = (variationName: string, quantity: number) => {
    setSelectedVariations(prev =>
      prev.map(v => {
        if (v.variation === variationName) {
          return { ...v, quantity: Math.max(0, quantity) };
        }
        return v;
      })
    );
  };

  const handleQuantityPricingClick = (variation: any, tier: any) => {
    const maxQuantity = parseInt(tier.maxQuantity || tier.minQuantity || '1');
    setVariationQuantity(variation.variation, maxQuantity);
  };

  const isQuantityPricingSelected = (variation: any, tier: any) => {
    const selected = selectedVariations.find(v => v.variation === variation.variation);
    if (!selected) return false;
    const maxQuantity = parseInt(tier.maxQuantity || tier.minQuantity || '1');
    return selected.quantity >= parseInt(tier.minQuantity || '1') && selected.quantity <= maxQuantity;
  };

  const isInWishlist = product ? wishlist.includes(product.id) : false;

  const resolveImageUrl = (imagePath?: string | null) => {
    if (!imagePath) return null;
    const trimmed = imagePath.trim();
    if (trimmed.match(/^https?:\/\//i)) return trimmed;
    if (trimmed.includes('img.cultaz.com')) return trimmed;
    return `https://img.cultaz.com/${trimmed.replace(/^\/+/, '')}`;
  };

  const handleVariationImageClick = (variation: any) => {
    const img = resolveImageUrl(variation.variation_img);
    if (!img) return;

    setZoomImage(img);
    setShowZoomModal(true);
  };

  const handlePrevImage = () => {
    if (galleryImages.length > 0) {
      const newIndex = (currentImageIndex - 1 + galleryImages.length) % galleryImages.length;
      setCurrentImageIndex(newIndex);
    }
  };

  const handleNextImage = () => {
    if (galleryImages.length > 0) {
      const newIndex = (currentImageIndex + 1) % galleryImages.length;
      setCurrentImageIndex(newIndex);
    }
  };

  const handleImageClick = (image: string, index: number) => {
    setZoomImage(image);
    setZoomImageIndex(index);
    setShowZoomModal(true);
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentImageIndex(index);
  };

  const handleZoomPrev = () => {
    if (galleryImages.length > 0) {
      const newIndex = (zoomImageIndex - 1 + galleryImages.length) % galleryImages.length;
      setZoomImageIndex(newIndex);
      setZoomImage(galleryImages[newIndex]);
    }
  };

  const handleZoomNext = () => {
    if (galleryImages.length > 0) {
      const newIndex = (zoomImageIndex + 1) % galleryImages.length;
      setZoomImageIndex(newIndex);
      setZoomImage(galleryImages[newIndex]);
    }
  };

  const handleZoomKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') handleZoomPrev();
    if (e.key === 'ArrowRight') handleZoomNext();
    if (e.key === 'Escape') setShowZoomModal(false);
  };

  const handleShare = () => {
    if (!product) return;
    const shareData = {
      title: product.produt_name,
      text: product.description,
      url: window.location.href,
    };
    if (navigator.share) {
      navigator.share(shareData).catch(() => {
        toast('Share was cancelled or is not supported.', { icon: 'ℹ️' });
      });
    } else {
      navigator.clipboard.writeText(`${shareData.title} - ${shareData.url}`);
      toast.success('Product link copied to clipboard');
    }
  };

  useEffect(() => {
    if (!product) return;

    const baseImages = (product.images || []).map(resolveImageUrl).filter((img): img is string => !!img);
    const variationImages = (product.variations || [])
      .map(v => resolveImageUrl(v.variation_img))
      .filter((img): img is string => !!img);

    setGalleryImages(Array.from(new Set([...baseImages, ...variationImages])));
    setCurrentImageIndex(0);
    
    // Preload all images for instant switching
    const preloadImages = async () => {
      const allImages = Array.from(new Set([...baseImages, ...variationImages]));
      const loadPromises = allImages.map((src, index) => {
        return new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => {
            setImagesLoaded(prev => new Set(prev).add(index));
            resolve();
          };
          img.onerror = () => resolve(); // Still resolve on error to not block
          img.src = src;
        });
      });
      
      try {
        await Promise.all(loadPromises);
      } catch (error) {
        console.log('Some images failed to preload');
      }
    };
    
    preloadImages();
  }, [product]);

  useEffect(() => {
    const swiper = swiperRef.current;
    if (swiper && selectedImageIndex >= 0 && swiper.activeIndex !== selectedImageIndex) {
      swiper.slideTo(selectedImageIndex, 300);
    }
  }, [selectedImageIndex]);

  if (loading) return <ProductDetailSkeleton />;
  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product not found</h2>
          <button onClick={() => navigate('/products')} className="btn-primary">
            Browse Products
          </button>
        </div>
      </div>
    );
  }

  const hasVariations = product.variations && product.variations.length > 0;
  const discountInfo = calculateProductDiscount(product, quantity);

  return (
    <>
      <Helmet>
        <title>{product.produt_name} | Leeyah Trend - Ladies Fashion & More</title>
        <meta name="description" content={product.description || 'Shop this stylish item along with clothing, bags, shoes, accessories, beauty products and gadgets for ladies at Leeyah Trend...slay efortlessly.'} />
      </Helmet>

      <div className="min-h-screen bg-white pb-24 md:pb-0">
        {/* FULL WIDTH HERO SECTION */}
        <section className="bg-gradient-to-b from-gray-50 to-white border-b border-gray-200">
          {/* Back button - always visible, above layout */}
          <div className="max-w-7xl mx-auto px-4 md:px-8 pt-4 md:pt-6">
            <motion.button
              whileHover={{ x: -4 }}
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900 text-sm font-medium bg-white/80 backdrop-blur px-3 py-1.5 rounded-full shadow-sm border border-gray-200"
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </motion.button>
          </div>
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-0 items-stretch min-h-[380px] md:min-h-[520px]">
            {/* === LEFT SIDE: LARGE IMAGE CAROUSEL === */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="relative flex items-center justify-center bg-gray-50 md:rounded-r-3xl overflow-hidden"
            >
              {galleryImages.length > 0 ? (
                <div className="relative w-full h-full flex items-center justify-center bg-gray-50">
                  {/* Main Image */}
                  <div className="relative w-full h-full flex items-center justify-center p-2 sm:p-4 pr-20">
                    <button
                      type="button"
                      onClick={() => handleImageClick(galleryImages[currentImageIndex], currentImageIndex)}
                      className="w-full h-full flex items-center justify-center"
                    >
                      <img
                        src={galleryImages[currentImageIndex]}
                        alt={`${product.produt_name} ${currentImageIndex + 1}`}
                        className="max-w-full max-h-full object-contain hover:scale-105 transition-transform duration-300 cursor-pointer"
                        key={galleryImages[currentImageIndex]} // Force re-render but maintain cached image
                      />
                    </button>
                  </div>

                  {/* Navigation Buttons */}
                  {galleryImages.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={handlePrevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors border border-gray-200"
                      >
                        <ChevronLeft className="h-6 w-6 text-gray-700" />
                      </button>
                      <button
                        type="button"
                        onClick={handleNextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors border border-gray-200"
                      >
                        <ChevronRight className="h-6 w-6 text-gray-700" />
                      </button>
                    </>
                  )}

                  {/* Image Counter */}
                  {galleryImages.length > 1 && (
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-bold text-gray-900 shadow-sm">
                      {currentImageIndex + 1} / {galleryImages.length}
                    </div>
                  )}

                  {/* Thumbnail Strip - Right Side */}
                  {galleryImages.length > 1 && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-2 bg-white/90 backdrop-blur-sm p-2 rounded-lg shadow-lg z-20 max-h-96 overflow-y-auto">
                      {galleryImages.map((image, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleThumbnailClick(index)}
                          className={`w-12 h-12 rounded overflow-hidden transition-all border-2 flex-shrink-0 ${
                            index === currentImageIndex
                              ? 'border-brand-500 scale-110 shadow-md'
                              : 'border-transparent opacity-70 hover:opacity-100 hover:scale-105'
                          }`}
                        >
                          <img
                            src={image}
                            alt={`Thumbnail ${index + 1}`}
                            className="w-full h-full object-cover"
                            loading="eager" // Preload thumbnails
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NoImagePlaceholder className="w-full h-full rounded-none" size="lg" />
              )}
            </motion.div>

            {/* === RIGHT SIDE: STICKY PRODUCT INFO === */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex flex-col overflow-y-auto max-h-screen md:sticky md:top-0 p-4 md:p-6 space-y-4 md:border-l border-gray-200"
            >
              {/* Category Badge */}
              <div>
                <span className="inline-block text-xs font-bold text-brand-600 uppercase tracking-wide mb-1">
                  {product.category || 'Hair Care'} • Premium Collection
                </span>
              </div>

              {/* Title & Price Header */}
              <div>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-2xl md:text-3xl font-display font-bold text-gray-900 mb-2 leading-tight"
                >
                  {product.produt_name}
                </motion.h1>

                {/* Price & Discount */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex items-baseline gap-3 mb-2"
                >
                  <div className="text-3xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-600 to-brand-500">
                    {formatPrice(product.sellingprice)}
                  </div>
                  {discountInfo.hasDiscount && (
                    <div className="text-lg text-gray-400 line-through font-semibold">
                      {formatPrice(parseFloat(product.sellingprice) * 1.2)}
                    </div>
                  )}
                </motion.div>

                {/* Badges */}
                <div className="flex flex-wrap gap-2 mb-2">
                  {discountInfo.hasDiscount && (
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-red-100 text-red-700">
                      <Zap className="h-4 w-4" />
                      {discountInfo.discountType === 'percentage' 
                        ? `${discountInfo.discountAmount}% OFF` 
                        : `Save ${formatPrice(discountInfo.discountAmount)}`}
                    </div>
                  )}
                  {product.postType === "1" && (
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-purple-100 text-purple-700">
                      Pre-Order
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Features */}
              <div className="flex items-center justify-center gap-4 py-1">
                {[
                  { icon: Package, label: 'Premium' },
                  { icon: Truck, label: 'Fast' },
                  { icon: Shield, label: 'Secure' }
                ].map((feature, idx) => (
                  <motion.div
                    key={feature.label}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 + idx * 0.05 }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 rounded-full border border-gray-200"
                  >
                    <feature.icon className="h-4 w-4 text-brand-600" />
                    <span className="text-xs font-medium text-gray-700">{feature.label}</span>
                  </motion.div>
                ))}
              </div>

              {/* Divider */}
              <div className="h-0.5 bg-gradient-to-r from-brand-200 via-brand-100 to-gray-50" />

              {/* Variations */}
              {hasVariations && product.variations!.some(v => v.variation.toLowerCase() !== 'nil') && (
                <div className="space-y-3">
                  <h3 className="text-lg font-bold text-gray-900">Choose Options</h3>
                  <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-2">
                    {product.variations!
                      .filter(v => v.variation.toLowerCase() !== 'nil')
                      .sort((a, b) => {
                        const aQty = parseInt((a as any).v_qty || '0');
                        const bQty = parseInt((b as any).v_qty || '0');
                        if (aQty > 0 && bQty === 0) return -1;
                        if (aQty === 0 && bQty > 0) return 1;
                        return 0;
                      })
                      .map((variation, idx) => {
                        const selected = selectedVariations.find(v => v.variation === variation.variation);
                        const varPrice = (variation as any).variation_amount || 0;
                        const basePrice = varPrice > 0 ? Number(varPrice) : Number(product.sellingprice || 0);
                        const selectedQty = selected?.quantity || 0;
                        const effectiveVariationPrice = selectedQty > 0
                          ? getEffectiveVariationPrice(product, { ...variation, quantity: selectedQty }, selectedVariations)
                          : basePrice;
                        const displayPrice = selectedQty > 0 ? effectiveVariationPrice : basePrice;
                        const availableQty = parseInt((variation as any).v_qty || '0');
                        const isSoldOut = availableQty === 0;
                        
                        return (
                          <motion.div
                            key={variation.v_id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.03 }}
                            className={`p-4 rounded-2xl border-2 transition-all ${
                              isSoldOut
                                ? 'bg-gray-50 border-gray-200 opacity-60'
                                : selected && selected.quantity > 0
                                ? 'bg-gradient-to-br from-brand-50 to-brand-100 border-brand-400 shadow-lg'
                                : 'bg-white border-gray-300 hover:border-brand-300'
                            }`}
                          >
                            <div className="space-y-3">
                              <div className="flex items-start gap-3">
                                {variation.variation_img && (
                                  <button
                                    type="button"
                                    onClick={() => handleVariationImageClick(variation)}
                                    className="shrink-0"
                                  >
                                    <img
                                      src={resolveImageUrl(variation.variation_img) || ''}
                                      alt={variation.variation}
                                      className="w-16 h-16 rounded-md object-cover border border-gray-200 hover:border-brand-400 transition-colors cursor-pointer"
                                    />
                                  </button>
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className={`font-semibold ${isSoldOut ? 'text-gray-500' : 'text-gray-900'} break-words`}>
                                      {variation.variation}
                                    </p>
                                    <p className={`text-sm ${isSoldOut ? 'text-gray-400' : 'text-brand-600 font-bold'}`}>
                                      {formatPrice(displayPrice)}
                                    </p>
                                    {selectedQty > 0 && effectiveVariationPrice !== basePrice && (
                                      <p className="text-xs text-gray-500 mt-0.5">
                                        {selectedQty} pcs price: {formatPrice(effectiveVariationPrice)}
                                      </p>
                                    )}
                                  </div>
                              </div>

                              <div className="flex items-center justify-center gap-4">
                                <button
                                  onClick={() => updateVariationQuantity(variation.variation, -1)}
                                  disabled={!selected || selected.quantity === 0}
                                  className="w-10 h-10 rounded-full bg-white border-2 border-gray-300 text-gray-600 hover:border-brand-400 hover:text-brand-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors shadow-sm"
                                >
                                  <Minus className="h-5 w-5" />
                                </button>
                                <span className="text-lg font-bold w-8 text-center text-gray-900">{selected?.quantity || 0}</span>
                                <button
                                  onClick={() => updateVariationQuantity(variation.variation, 1)}
                                  disabled={selected && selected.quantity >= availableQty}
                                  className="w-10 h-10 rounded-full bg-brand-500 border-2 border-brand-500 text-white hover:bg-brand-600 hover:border-brand-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors shadow-sm"
                                >
                                  <Plus className="h-5 w-5" />
                                </button>
                              </div>
                            </div>

                          {variation.hasQuantityPricing && variation.quantityPricing && variation.quantityPricing.length > 0 && (
                            <div className="mt-3 text-sm">
                              <div className="flex flex-wrap gap-2">
                                {variation.quantityPricing.map((tier, tierIndex) => (
                                  <button
                                    key={`tier-${variation.v_id}-${tierIndex}`}
                                    onClick={() => handleQuantityPricingClick(variation, tier)}
                                    className={`shrink-0 whitespace-nowrap rounded-full border text-xs px-3 py-1.5 transition-all ${
                                      isQuantityPricingSelected(variation, tier)
                                        ? 'border-brand-500 bg-brand-500 text-white shadow-md'
                                        : 'border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100'
                                    }`}
                                  >
                                    {tier.minQuantity}-{tier.maxQuantity} {formatPrice(tier.price)}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          </motion.div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Quantity Selector (Non-variation) */}
              {(!hasVariations || product.variations!.every(v => v.variation.toLowerCase() === 'nil')) && (
                <div className="bg-gradient-to-br from-brand-50 to-white rounded-2xl p-5 border-2 border-brand-200">
                  <h3 className="font-bold text-gray-900 mb-4">Quantity</h3>
                  <div className="flex items-center gap-3 bg-white border border-brand-300 rounded-xl p-2 w-fit">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-9 h-9 rounded-lg bg-gray-100 hover:bg-brand-100 flex items-center justify-center"
                    >
                      <Minus className="h-5 w-5" />
                    </button>
                    <span className="text-2xl font-bold w-12 text-center text-gray-900">{quantity}</span>
                    <button
                      onClick={() => setQuantity(Math.min(product.totalStock, quantity + 1))}
                      disabled={quantity >= product.totalStock}
                      className="w-9 h-9 rounded-lg bg-brand-500 hover:bg-brand-600 disabled:opacity-50 flex items-center justify-center text-white"
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}

              {/* Desktop CTA Buttons */}
              <div className="hidden md:flex gap-3 pt-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => toggleWishlist(product.id)}
                  className={`w-14 h-14 rounded-2xl flex items-center justify-center border-2 transition-all font-bold text-lg ${
                    isInWishlist
                      ? 'bg-brand-100 border-brand-500 text-brand-600'
                      : 'bg-white border-gray-300 text-gray-400 hover:border-brand-400 hover:text-brand-400'
                  }`}
                >
                  <Heart className={`h-6 w-6 ${isInWishlist ? 'fill-current' : ''}`} />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleShare}
                  className="w-14 h-14 rounded-2xl flex items-center justify-center border-2 bg-white text-gray-500 hover:border-brand-400 hover:text-brand-400"
                >
                  <Share2 className="h-6 w-6" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleAddToCart}
                  disabled={storeInfo?.site_lock === 1 || isAdding}
                  className="flex-1 bg-gradient-to-r from-brand-600 to-brand-500 hover:shadow-2xl text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 text-lg shadow-lg transition-all relative overflow-hidden"
                >
                  <motion.div
                    initial={false}
                    animate={showAddedToCart ? { y: -50 } : { y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ShoppingCart className="h-6 w-6" />
                  </motion.div>
                  <motion.span
                    initial={false}
                    animate={showAddedToCart ? { y: -50 } : { y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {storeInfo?.site_lock === 1 ? 'Site Locked' : 'Add to Cart'}
                  </motion.span>
                  
                  {showAddedToCart && (
                    <motion.div
                      initial={{ y: 50, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: 50, opacity: 0 }}
                      className="absolute inset-0 flex items-center justify-center gap-2 bg-gradient-to-r from-green-600 to-green-500"
                    >
                      <Check className="h-6 w-6" />
                      <span>Added!</span>
                    </motion.div>
                  )}
                </motion.button>
              </div>

              {/* Description Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="pt-4 space-y-4"
              >
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">About This Product</h3>
                  <p className="text-gray-600 leading-relaxed line-clamp-5">
                    {product.description || 'No description available'}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>


      {/* MOBILE BOTTOM ACTIONS */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t-2 border-brand-100 z-40">
        <div className="grid grid-cols-2 gap-2 p-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => toggleWishlist(product.id)}
            className={`flex items-center justify-center gap-2 py-3 rounded-xl font-bold transition-colors ${
              isInWishlist
                ? 'bg-brand-100 text-brand-600 border-2 border-brand-600'
                : 'bg-gray-100 text-gray-700 border-2 border-gray-300'
            }`}
          >
            <Heart className={`h-5 w-5 ${isInWishlist ? 'fill-current' : ''}`} />
            Wishlist
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleAddToCart}
            disabled={storeInfo?.site_lock === 1 || isAdding}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-brand-600 to-brand-500 text-white font-bold py-3 rounded-xl transition-all"
          >
            <ShoppingCart className="h-5 w-5" />
            {storeInfo?.site_lock === 1 ? 'Site Locked' : 'Add'}
          </motion.button>
        </div>
      </div>

      </div>

      {/* Related Products Section */}
      {product && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RelatedProducts 
            currentProductId={product.id}
            currentProductCategory={product.category || 'Nil'}
            products={allProducts}
            maxItems={4}
            onAddToCart={(product) => {
              addToCart({
                ...product,
                quantity: 1,
                selectedVariations: []
              });
              toast.success('Added to cart', {
                duration: 3200,
                position: 'bottom-center',
                style: {
                  minWidth: '320px',
                  maxWidth: '520px',
                  borderRadius: '14px',
                  padding: '18px 20px',
                  fontSize: '1rem',
                  background: 'linear-gradient(90deg, #5b21b6, #8b5cf6)',
                  color: '#fff',
                  boxShadow: '0 12px 28px rgba(0,0,0,0.25)'
                },
                icon: '🛒'
              });
            }}
            onAddToWishlist={(product) => toggleWishlist(product.id)}
            currency={storeInfo?.currency_symbol || '₦'}
          />
        </div>
      )}

      {/* Zoom Modal */}
      {showZoomModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowZoomModal(false)}
          onKeyDown={handleZoomKeyDown}
          tabIndex={0}
        >
          <div className="relative max-w-4xl max-h-full">
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setShowZoomModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors p-2 z-10"
            >
              <X className="h-8 w-8" />
            </button>

            {/* Navigation Buttons */}
            {galleryImages.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleZoomPrev();
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors border border-white/30"
                >
                  <ChevronLeft className="h-6 w-6 text-white" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleZoomNext();
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors border border-white/30"
                >
                  <ChevronRight className="h-6 w-6 text-white" />
                </button>
              </>
            )}

            {/* Image Counter */}
            {galleryImages.length > 1 && (
              <div className="absolute -top-12 left-0 text-white text-sm font-medium bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full">
                {zoomImageIndex + 1} / {galleryImages.length}
              </div>
            )}

            {/* Main Image */}
            <img
              src={zoomImage}
              alt="Zoomed product image"
              className="max-w-full max-h-[80vh] object-contain rounded-lg cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Use shared AddedToCartModal for a prominent small modal on add-to-cart */}
      <AddedToCartModal
        open={showAddedToCart}
        onClose={() => setShowAddedToCart(false)}
        product={product ? {
          id: product.id,
          produt_name: product.produt_name,
          sellingprice: product.sellingprice,
          images: product.images || []
        } : { id: 0, produt_name: 'Product', sellingprice: '0', images: [] }}
        quantity={quantity}
        selectedVariations={selectedVariations.filter(v => v.quantity > 0)}
        onContinueShopping={() => setShowAddedToCart(false)}
        onGoToCart={() => { setShowAddedToCart(false); window.location.href = '/cart'; }}
      />
    </>
  );
}
